import { Injectable } from '@angular/core';
import type { EchartsConfig, ILaneConfig, LaneConfig, yAxisConfig } from 'commons';
import type { YAXisComponentOption } from 'echarts';
import type { DtisZoomEvent } from '../zoomEvent';
import { CHART_CONSTANTS } from './echarts-config.types';

/**
 * Service responsible for building Y-axis configurations for ECharts multilane charts.
 */
@Injectable({ providedIn: 'root' })
export class YAxisBuilderService {
  /**
   * Builds Y-axis configurations for all lanes
   */
  buildYAxes(lanes: LaneConfig[], currentYAxisPanel: string | null, config?: EchartsConfig): YAXisComponentOption[] {
    return lanes.flatMap((lane, index) => this.createYAxisComponents(lane, index, currentYAxisPanel, config));
  }

  /**
   * Applies boundaries to Y-axes based on series data and thresholds
   */
  async applyBoundaries(
    lanes: (LaneConfig | ILaneConfig)[],
    series: unknown[],
    yAxis: YAXisComponentOption[],
    zoom: DtisZoomEvent[]
  ): Promise<YAXisComponentOption[]> {
    const resolvedSeries = await Promise.all(series);

    // Skip boundary adjustment if vertical zoom is active
    if (zoom.some((z) => z.dataZoomId.includes('vertical'))) {
      return yAxis;
    }

    return yAxis.map((yOptions, yIndex) => this.applyAxisBoundaries(yOptions, yIndex, lanes, resolvedSeries));
  }

  private createYAxisComponents(laneConfig: LaneConfig, laneIndex: number, currentYAxisPanel: string | null, config?: EchartsConfig): YAXisComponentOption[] {
    // Compute units directly from controls to handle plain objects from NGXS state
    // (class getters like LaneConfig.units are lost when lanes are spread into plain objects in the state)
    const units = laneConfig?.units ?? laneConfig?.controls?.map((control) => control?.unit).filter((unit, index, arr) => arr.indexOf(unit) === index);

    if (!laneConfig?.config?.yAxises?.length) {
      return [this.createDefaultYAxis(units, laneIndex, config)];
    }

    return laneConfig.config.yAxises.map((yAxisConfig, axisIndex) => {
      // When a y-axis has specific controls assigned, derive units from those controls only
      const axisUnits = yAxisConfig.controls?.length
        ? laneConfig.controls
            ?.filter((c) => yAxisConfig.controls?.includes(c.id))
            .map((c) => c.unit)
            .filter((unit, index, arr) => arr.indexOf(unit) === index)
        : units;

      return this.createYAxisFromConfig(yAxisConfig, axisUnits, laneIndex, axisIndex, currentYAxisPanel, config);
    });
  }

  private createDefaultYAxis(units: (string | undefined)[] | undefined, gridIndex: number, config?: EchartsConfig): YAXisComponentOption {
    const precision = config?.yAxisLabelPrecision ?? 3;

    return {
      type: 'value',
      max: 100,
      min: 0,
      splitNumber: config?.yAxisSplitNumber ?? 2,
      alignTicks: false,
      splitLine: {
        show: config?.yAxisShowSplitLine ?? true,
        lineStyle: {
          color: config?.yAxisSplitLineColor ?? '#eee',
          type: config?.yAxisSplitLineType ?? 'solid',
        },
      },
      axisLabel: {
        width: '10px' as unknown as number,
        overflow: 'breakAll',
        hideOverlap: true,
        show: config?.yAxisShowLabels ?? true,
        fontSize: config?.yAxisLabelFontSize ?? 12,
        color: config?.yAxisLabelColor ?? '#333',
        formatter: (value: number) => value.toFixed(precision),
      },
      boundaryGap: this.formatBoundaryGap(config?.yAxisBoundaryGap),
      axisLine: {
        show: config?.yAxisShowLine ?? true,
        lineStyle: {
          width: config?.yAxisLineWidth ?? 1,
          color: config?.yAxisLineColor ?? '#333',
        },
      },
      nameLocation: 'middle',
      nameGap: config?.yAxisNameGap ?? 25,
      nameTextStyle: {
        fontSize: config?.yAxisNameFontSize ?? 12,
      },
      gridIndex,
      offset: config?.yAxisOffsetBase ?? 50,
      name: this.formatUnitName(units),
    };
  }

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO
  private createYAxisFromConfig(
    yAxisConfig: yAxisConfig,
    units: (string | undefined)[] | undefined,
    gridIndex: number,
    axisIndex: number,
    currentYAxisPanel: string | null,
    config?: EchartsConfig
  ): YAXisComponentOption {
    const baseOffset = config?.yAxisOffsetBase ?? 0;
    const isFocused = yAxisConfig.id === currentYAxisPanel;
    const precision = config?.yAxisLabelPrecision ?? 3;

    return {
      type: 'value',
      id: yAxisConfig.id,
      max: 100,
      min: 0,
      splitNumber: config?.yAxisSplitNumber ?? 2,
      alignTicks: false,
      splitLine: {
        show: config?.yAxisShowSplitLine ?? true,
        lineStyle: {
          color: config?.yAxisSplitLineColor ?? '#eee',
          type: config?.yAxisSplitLineType ?? 'solid',
        },
      },
      axisLabel: {
        width: '10px' as unknown as number,
        overflow: 'breakAll',
        hideOverlap: true,
        show: config?.yAxisShowLabels ?? true,
        fontSize: config?.yAxisLabelFontSize ?? 12,
        color: config?.yAxisLabelColor ?? '#333',
        formatter: (value: number) => value.toFixed(precision),
      },
      boundaryGap: this.formatBoundaryGap(config?.yAxisBoundaryGap),
      axisLine: {
        show: config?.yAxisShowLine ?? true,
        lineStyle: {
          width: isFocused ? (config?.yAxisFocusedWidth ?? 2) : (config?.yAxisLineWidth ?? 1),
          color: isFocused ? (config?.yAxisFocusedColor ?? '#ffa026') : (config?.yAxisLineColor ?? '#333'),
        },
      },
      nameLocation: 'middle',
      nameGap: config?.yAxisNameGap ?? 25,
      nameTextStyle: {
        fontSize: config?.yAxisNameFontSize ?? 12,
      },

      gridIndex,
      offset: axisIndex * baseOffset,
      ...yAxisConfig.settings,
      name: yAxisConfig.settings?.name || this.formatUnitName(units),
    } as YAXisComponentOption;
  }

  private applyAxisBoundaries(
    yOptions: YAXisComponentOption,
    yIndex: number,
    lanes: (LaneConfig | ILaneConfig)[],
    resolvedSeries: unknown[]
  ): YAXisComponentOption {
    if (!resolvedSeries) {
      return yOptions;
    }

    const { seriesValues, thresholdValues } = this.collectBoundaryValues(yIndex, lanes, resolvedSeries);

    const allValues = [...seriesValues, ...thresholdValues].filter((v) => Number.isFinite(v));

    if (allValues.length === 0) {
      return yOptions;
    }

    const [max, min] = [allValues.reduce((a, b) => Math.max(a, b), -Infinity), allValues.reduce((a, b) => Math.min(a, b), Infinity)];

    const yAxisConfig = this.findYAxisConfig(lanes, yOptions.id?.toString());

    return {
      ...yOptions,
      max: max ?? this.formatBoundaryValue(yAxisConfig?.settings?.max),
      min: min ?? this.formatBoundaryValue(yAxisConfig?.settings?.min),
      alignTicks: false,
    };
  }

  private collectBoundaryValues(
    yIndex: number,
    lanes: (LaneConfig | ILaneConfig)[],
    resolvedSeries: unknown[]
  ): { seriesValues: number[]; thresholdValues: number[] } {
    const seriesValues = resolvedSeries
      // biome-ignore lint/suspicious/noExplicitAny: echarts
      .filter((serie: any) => yIndex === serie?.yAxisIndex)
      // biome-ignore lint/suspicious/noExplicitAny: echarts
      .flatMap((serie: any) => serie?.data?.map((s: any) => s[1]) ?? [])
      .filter((s): s is number => s != null && !Number.isNaN(s));

    const thresholdValues = lanes[yIndex]?.config?.thresholds?.map((lt) => Number(lt?.value)) ?? [];

    return { seriesValues, thresholdValues };
  }

  private findYAxisConfig(lanes: (LaneConfig | ILaneConfig)[], yAxisId?: string): yAxisConfig | undefined {
    for (const lane of lanes) {
      const found = lane.config.yAxises?.find((y) => y.id === yAxisId);
      if (found) {
        return found;
      }
    }
    return undefined;
  }

  private formatBoundaryValue(value: unknown): number | string {
    if (typeof value === 'number') {
      return Number(value).toFixed(CHART_CONSTANTS.DECIMAL_PLACE);
    }
    return 'auto';
  }

  private formatBoundaryGap(percentage = 20): [string, string] {
    return [`${percentage}%`, `${percentage}%`];
  }

  private formatUnitName(units: (string | undefined)[] | undefined): string | undefined {
    if (!units || units.length === 0) {
      return undefined;
    }
    return units.length > 1 ? 'multiple units' : units[0];
  }
}
